import React, { useState } from 'react';
import { useI18nContext } from '@ecg-internal/js-react-i18n';
import { UtilsBlock } from '@hz-design-system/web-ui';

import requestStatus, { type RequestStatus } from '../../constants/requestStatus';
import Notifications from '../../components/Notifications/Notifications';
import ProfileHero from '../../components/ProfileHero/ProfileHero';
import ProfileBody from '../../components/ProfileBody/ProfileBody';
import ProfileSidebar from '../../components/ProfileSidebar/ProfileSidebar';
import InactiveSellerProfileBody from '../../components/ProfileBody/InactiveSellerProfileBody';
import saveSellerRequest from '../../services/saveSeller';
import deleteSellerRequest from '../../services/deleteSeller';
import { EnvironmentProvider } from '../../contexts/EnvironmentContext';
import type { AppState, EnvironmentConfig } from '../../../../../types/AppState';

import './SellerProfile.scss';

const SellerProfileApp = ({ appState, environment }: { appState: AppState; environment: EnvironmentConfig }) => {
  const { isUserViewingOwnProfile, editProfileUrl, profile, loggedInUser, notifications } = appState;
  const { xsrfToken } = environment;
  const i18n = useI18nContext();

  const {
    id,
    hasSellerProfile,
    description,
    dsaFlags,
    title,
    contactInformation: { city, postCode, street, email, phoneNumber },
    companyRegistrationData,
    businessHours,
    outsideBusinessHourAvailable,
    callAvailabilityHours,
    logo,
    isSavedSeller,
    hasGeolocation,
    sellerGeolocation,
    uniqueSellingPoints,
    websiteUrl,
    isFinanceAvailable,
    companyVideoUrl,
    reviews,
    awards,
    brands,
    salesRepresentatives,
    showFranchiseDealerIndicator,
    workshopServices,
    allWorkshopServices,
  } = profile;

  const [saveSellerRequestStatus, setSellerRequestStatus] = useState<RequestStatus>(requestStatus.initial);
  const [isSellerSaved, setSellerAsSaved] = useState(isSavedSeller);
  const [notification, setNotification] = useState('');

  const translatedText = {
    saveSellerSuccessMessage: i18n.t('notifications.saveSellerSuccess', { url: '/mymp/favoriete-verkopers.html' }),
    deleteSellerSuccessMessage: i18n.t('notifications.deleteSellerSuccess', { url: '/mymp/favoriete-verkopers.html' }),
    genericErrorMessage: i18n.t('notifications.genericError'),
  };

  const saveSeller = async () => {
    try {
      await saveSellerRequest({ xsrfToken, sellerId: id });
      setSellerRequestStatus(requestStatus.success);
      setSellerAsSaved(true);
      setNotification(translatedText.saveSellerSuccessMessage);
    } catch {
      setSellerRequestStatus(requestStatus.failure);
      setNotification(translatedText.genericErrorMessage);
    } finally {
      window.scroll(0, 0);
    }
  };

  const deleteSeller = async () => {
    try {
      await deleteSellerRequest({ xsrfToken, sellerId: id });
      setSellerRequestStatus(requestStatus.success);
      setSellerAsSaved(false);
      setNotification(translatedText.deleteSellerSuccessMessage);
    } catch {
      setSellerRequestStatus(requestStatus.failure);
      setNotification(translatedText.genericErrorMessage);
    } finally {
      window.scroll(0, 0);
    }
  };

  const redirectToLogInScreen = () =>
    window.location.assign(`/account/login.html?target=${encodeURIComponent(window.location.href)}`);

  const onClickSaveControl = () => {
    if (!loggedInUser) {
      return redirectToLogInScreen();
    }
    const toggleSaveSeller = isSellerSaved ? deleteSeller : saveSeller;
    return toggleSaveSeller();
  };

  return (
    <EnvironmentProvider value={environment}>
      <div>
        <Notifications
          notification={notification}
          notifications={notifications}
          saveSellerRequestStatus={saveSellerRequestStatus}
        />
        <UtilsBlock marginBottom="s">
          <ProfileHero
            isUserViewingOwnProfile={isUserViewingOwnProfile}
            sellerProfile={profile}
            editProfileUrl={editProfileUrl}
            loggedInUserId={loggedInUser && loggedInUser.id}
            onClickSaveControl={onClickSaveControl}
            isSellerSaved={isSellerSaved}
            saveControlLoading={false}
            reviews={reviews}
          />
        </UtilsBlock>
        <div className="contentWrap">
          <div className="leftCol">
            {hasSellerProfile ? (
              <ProfileBody
                sellerId={id}
                sellerTitle={title}
                description={description}
                isFinanceAvailable={isFinanceAvailable}
                showFranchiseDealerIndicator={showFranchiseDealerIndicator}
                uniqueSellingPoints={uniqueSellingPoints}
                companyVideoUrl={companyVideoUrl}
                awards={awards}
                brands={brands}
                workshopServices={workshopServices}
                allWorkshopServices={allWorkshopServices}
              />
            ) : (
              <InactiveSellerProfileBody sellerProfile={profile} />
            )}
          </div>
          <aside className="rightCol">
            <ProfileSidebar
              dsaFlags={dsaFlags}
              sellerTitle={title}
              contactInformation={{ street, postCode, city, email, phoneNumber }}
              companyRegistrationData={companyRegistrationData}
              logoImage={logo}
              hasGeolocation={hasGeolocation}
              sellerGeolocation={sellerGeolocation}
              websiteUrl={websiteUrl}
              salesRepresentatives={salesRepresentatives}
              businessHours={businessHours}
              callAvailabilityHours={callAvailabilityHours}
              outsideBusinessHourAvailable={outsideBusinessHourAvailable}
            />
          </aside>
        </div>
      </div>
    </EnvironmentProvider>
  );
};

export default SellerProfileApp;
